// ============================================================
// THE REST OF ITS TRAITS, DRAWN (plan body-map T2; engine/plate/marks):
// Natural Armour's scales (flashing where a hit lands), Sturdy's planted
// feet (the floor cracking under a hit), Swift's streaks and the ghost a
// step behind, Frenzied's tensed red outline (its tremor is the figure's
// class: ./TraitLayers), Blind's listening. Deadly Natural Weapons is the
// toxic fists' pulse in blood red (./Toxic `Fists`); Black Carapace and
// Strange Physiology are the sights' (plateParts `PlateSights`).
// ============================================================

import { OutlineFilter } from './Outline';
import type { CSSProperties } from 'react';
import { anchorOf, type Box, type PlateFrame } from '../../../../engine/plate/frame';

type Placed = Pick<PlateFrame, 'anchors' | 'box'>;
import { landed } from '../../../../engine/plate/burst';
import { listenArcs, scalesLook, sturdyCracks, sturdyFeet, swiftStreaks } from '../../../../engine/plate/marks';
import tx from '../../../../styles/PlateTraits.module.css';
import { tracked } from '../../usePlateClock';
import type { HitPlay } from '../../usePlateHits';
import { MASK_RECT } from '../conditions';
import { SoftDisc } from './Toxic';
import { timed } from '../timing';

/** One tile of scales: three arcs, a row offset by half a scale (`size` wide, `h` high). */
const scaleRow = (size: number, h: number) =>
  [`M0 ${h}`, `M${-size / 2} ${h / 2}`, `M${size / 2} ${h / 2}`]
    .map((m) => `${m} q${size / 2} ${-h} ${size} 0`)
    .join(' ');

/** Natural Armour's scales and its silhouette mask; Frenzied's tensed outline. */
export function MarkDefs({ u, scales, tense }: { u: string; scales: number; tense: boolean }) {
  const { size } = scalesLook(scales);
  const h = size * 0.6;
  return (
    <>
      {scales > 0 && (
        <>
          <pattern id={`${u}-scales`} width={size} height={h} patternUnits="userSpaceOnUse">
            <path className={tx.scale} d={scaleRow(size, h)} />
          </pattern>
          <SoftDisc id={`${u}-scfg`} from={0.45} />
          <mask id={`${u}-scm`} maskUnits="userSpaceOnUse" {...MASK_RECT}>
            <use href={`#${u}-sil`} className={tx.maskWhite} />
          </mask>
        </>
      )}
      {tense && <OutlineFilter id={`${u}-tense`} radius={14} flood={tx.frenzyFlood} />}
    </>
  );
}

/** The scales laid over its body (its silhouette's mask). */
const Scales = ({ u, opacity }: { u: string; opacity: number }) => (
  <rect
    x={-800}
    y={-200}
    width={3800}
    height={3600}
    fill={`url(#${u}-scales)`}
    mask={`url(#${u}-scm)`}
    opacity={opacity}
  />
);

/** Natural Armour (X): scales over its body; where each landed hit strikes, they flash white as they turn it. */
export function ArmourFront({
  u,
  x,
  play,
  place,
}: {
  u: string;
  x: number;
  play: HitPlay | null;
  /** Where the figure is struck: its anchors and bounds. */
  place: Placed;
}) {
  return (
    <g pointerEvents="none" data-trait="natural-armour" data-armour={x}>
      <Scales u={u} opacity={scalesLook(x).opacity} />
      {play &&
        landed(play.burst.hits).map((h, i) => {
          const [hx, hy] = anchorOf(place, h.location);
          const id = `${u}-scf${i}`;
          return (
            <g key={`${play.id}:${i}`}>
              <mask id={id} maskUnits="userSpaceOnUse" {...MASK_RECT}>
                <circle cx={hx} cy={hy} r={260} fill={`url(#${u}-scfg)`} />
              </mask>
              <g mask={`url(#${id})`} className={tx.scaleFlash} style={timed(h.at)}>
                <Scales u={u} opacity={1} />
              </g>
            </g>
          );
        })}
    </g>
  );
}

/** Sturdy: its feet planted; under a landed hit the floor cracks out from them both ways. */
export function SturdyFront({ box, play }: { box: Box; play: HitPlay | null }) {
  const first = play && landed(play.burst.hits)[0];
  return (
    <g pointerEvents="none" data-trait="sturdy">
      {sturdyFeet(box).map((f, k) => (
        <path key={k} className={tx.sturdyFoot} d={f.d} />
      ))}
      {first &&
        sturdyCracks(box, play!.id).map((d, k) => (
          <path key={`${play!.id}:${k}`} className={tx.sturdyCrack} d={d} style={timed(first.at)} />
        ))}
    </g>
  );
}

/**
 * Swift, under the figure: streaks off its back edge, and its ghost a step behind - a copy of its still lines, never of
 * the moving figure (a `<use>` of that is re-cloned, every look in it, on every frame it moves).
 */
export function SwiftUnder({ u, box }: { u: string; box: Box }) {
  return (
    <g pointerEvents="none" data-trait="swift">
      {swiftStreaks(box).map((s, k) => (
        <line key={k} className={tx.swiftStreak} x1={s.x1} y1={s.y} x2={s.x2} y2={s.y} {...tracked([s.opacity])} />
      ))}
      <g className={tx.swiftGhost} transform="translate(-90 0)">
        <use href={`#${u}-art`} fill="currentColor" />
      </g>
    </g>
  );
}

/** Frenzied, behind the figure: its outline tensed red, as if every muscle strained. */
export function FrenzyBack({ u }: { u: string }) {
  return (
    <g pointerEvents="none" data-trait="frenzied" opacity={0.7}>
      <use href={`#${u}-sil`} className={tx.silFill} filter={`url(#${u}-tense)`} />
    </g>
  );
}

/** Blind, listening: sound drawn in from both sides to its head, and the head's side it reaches lit. */
export function ListenFront({ u, head }: { u: string; head: Box }) {
  return (
    <g pointerEvents="none" data-trait="blind">
      {listenArcs(head).map((a, k) => (
        <path
          key={k}
          className={tx.listenArc}
          d={`M0 -60 q${-a.side * 40} 60 0 120`}
          style={
            {
              '--from': `${a.from}px`,
              '--to': `${a.to}px`,
              '--y': `${a.y}px`,
              animationDelay: `-${a.offset}s`,
            } as CSSProperties
          }
        />
      ))}
      <g clipPath={`url(#${u}-c-head)`} className={tx.listenLit}>
        <g className={tx.listenLines}>
          <use href={`#${u}-art`} />
        </g>
      </g>
    </g>
  );
}
